import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Grid,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  Refresh,
  Visibility,
  VisibilityOff,
  ContentCopy,
  Security,
  Speed,
  Schedule,
  Computer,
  Language,
  ExpandMore,
  CheckCircle,
  Cancel,
  Warning,
} from '@mui/icons-material';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

interface Permissions {
  'prompt.process': boolean;
  'prompt.check': boolean;
  'prompt.history': boolean;
  'dashboard.view': boolean;
  'rules.manage': boolean;
}

interface ApiKey {
  id: number;
  name: string;
  permissions: Permissions;
  rate_limit: number;
  rate_limit_window: number;
  is_active: boolean;
  last_used: string | null;
  usage_count: number;
  allowed_ips: string[];
  allowed_domains: string[];
  expires_at: string | null;
  created_at: string;
  updated_at: string;
  is_expired: boolean;
  key_preview: string;
}

interface NewApiKey extends ApiKey {
  key: string;
}

const ApiKeys: React.FC = () => {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState<ApiKey | null>(null);
  const [newKeyData, setNewKeyData] = useState<NewApiKey | null>(null);
  const [showNewKey, setShowNewKey] = useState(false);
  const { isAuthenticated } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    rate_limit: 100,
    rate_limit_window: 3600,
    expires_in_days: '',
    allowed_ips: '',
    allowed_domains: '',
    permissions: {
      'prompt.process': true,
      'prompt.check': true,
      'prompt.history': true,
      'dashboard.view': false,
      'rules.manage': false,
    },
  });

  useEffect(() => {
    if (isAuthenticated) {
      fetchApiKeys();
    }
  }, [isAuthenticated]);

  const fetchApiKeys = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/keys');
      setApiKeys(response.data.api_keys || []);
    } catch (error: any) {
      console.error('Failed to fetch API keys:', error);
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async () => {
    try {
      const payload = {
        ...formData,
        allowed_ips: formData.allowed_ips ? formData.allowed_ips.split(',').map(ip => ip.trim()) : [],
        allowed_domains: formData.allowed_domains ? formData.allowed_domains.split(',').map(domain => domain.trim()) : [],
        expires_in_days: formData.expires_in_days ? parseInt(formData.expires_in_days) : null,
      };

      const response = await axios.post('/api/keys', payload);
      setNewKeyData(response.data.api_key);
      setShowNewKey(true);
      setCreateDialogOpen(false);
      fetchApiKeys();
      toast.success('API key created successfully!');
      resetForm();
    } catch (error: any) {
      console.error('Failed to create API key:', error);
      toast.error(error.response?.data?.error || 'Failed to create API key');
    }
  };

  const handleUpdateKey = async () => {
    if (!selectedKey) return;

    try {
      const payload = {
        name: formData.name,
        permissions: formData.permissions,
        rate_limit: formData.rate_limit,
        rate_limit_window: formData.rate_limit_window,
        allowed_ips: formData.allowed_ips ? formData.allowed_ips.split(',').map(ip => ip.trim()) : [],
        allowed_domains: formData.allowed_domains ? formData.allowed_domains.split(',').map(domain => domain.trim()) : [],
      };

      await axios.put(`/api/keys/${selectedKey.id}`, payload);
      setEditDialogOpen(false);
      fetchApiKeys();
      toast.success('API key updated successfully!');
      resetForm();
    } catch (error: any) {
      console.error('Failed to update API key:', error);
      toast.error(error.response?.data?.error || 'Failed to update API key');
    }
  };

  const handleDeleteKey = async (keyId: number) => {
    if (!window.confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`/api/keys/${keyId}`);
      fetchApiKeys();
      toast.success('API key deleted successfully');
    } catch (error: any) {
      console.error('Failed to delete API key:', error);
      toast.error(error.response?.data?.error || 'Failed to delete API key');
    }
  };

  const handleRegenerateKey = async (keyId: number) => {
    if (!window.confirm('Are you sure you want to regenerate this API key? The old key will stop working immediately.')) {
      return;
    }

    try {
      const response = await axios.post(`/api/keys/${keyId}/regenerate`);
      setNewKeyData(response.data.api_key);
      setShowNewKey(true);
      fetchApiKeys();
      toast.success('API key regenerated successfully!');
    } catch (error: any) {
      console.error('Failed to regenerate API key:', error);
      toast.error(error.response?.data?.error || 'Failed to regenerate API key');
    }
  };

  const handleEditKey = (key: ApiKey) => {
    setSelectedKey(key);
    setFormData({
      name: key.name,
      rate_limit: key.rate_limit,
      rate_limit_window: key.rate_limit_window,
      expires_in_days: '',
      allowed_ips: key.allowed_ips.join(', '),
      allowed_domains: key.allowed_domains.join(', '),
      permissions: { ...key.permissions },
    });
    setEditDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      rate_limit: 100,
      rate_limit_window: 3600,
      expires_in_days: '',
      allowed_ips: '',
      allowed_domains: '',
      permissions: {
        'prompt.process': true,
        'prompt.check': true,
        'prompt.history': true,
        'dashboard.view': false,
        'rules.manage': false,
      },
    });
    setSelectedKey(null);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusChip = (key: ApiKey) => {
    if (key.is_expired) {
      return <Chip label="Expired" color="error" size="small" icon={<Cancel />} />;
    }
    if (!key.is_active) {
      return <Chip label="Inactive" color="default" size="small" icon={<Cancel />} />;
    }
    return <Chip label="Active" color="success" size="small" icon={<CheckCircle />} />;
  };

  const permissionLabels: Record<keyof Permissions, string> = {
    'prompt.process': 'Process Prompts',
    'prompt.check': 'Check Prompts',
    'prompt.history': 'View History',
    'dashboard.view': 'View Dashboard',
    'rules.manage': 'Manage Rules',
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          API Key Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Create API Key
        </Button>
      </Box>

      <Typography variant="body1" color="textSecondary" paragraph>
        Manage your API keys for programmatic access to GuardLLM. 
        API keys allow external applications to interact with your security services.
      </Typography>

      {/* API Keys Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Rate Limit</TableCell>
              <TableCell>Usage</TableCell>
              <TableCell>Last Used</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {apiKeys.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="textSecondary">
                    No API keys found. Create your first API key to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              apiKeys.map((key) => (
                <TableRow key={key.id}>
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2">{key.name}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {key.key_preview}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{getStatusChip(key)}</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Speed fontSize="small" />
                      <Typography variant="body2">
                        {key.rate_limit}/{Math.floor(key.rate_limit_window / 3600)}h
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{key.usage_count} requests</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {key.last_used ? formatDate(key.last_used) : 'Never'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatDate(key.created_at)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEditKey(key)}>
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Regenerate">
                        <IconButton size="small" onClick={() => handleRegenerateKey(key.id)}>
                          <Refresh />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDeleteKey(key.id)} color="error">
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create API Key Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New API Key</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="API Key Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Production App, Development Testing"
                required
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Rate Limit (requests)"
                type="number"
                value={formData.rate_limit}
                onChange={(e) => setFormData({ ...formData, rate_limit: parseInt(e.target.value) })}
                inputProps={{ min: 1, max: 10000 }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Time Window (seconds)"
                type="number"
                value={formData.rate_limit_window}
                onChange={(e) => setFormData({ ...formData, rate_limit_window: parseInt(e.target.value) })}
                inputProps={{ min: 60, max: 86400 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Expires in Days (optional)"
                type="number"
                value={formData.expires_in_days}
                onChange={(e) => setFormData({ ...formData, expires_in_days: e.target.value })}
                placeholder="Leave empty for no expiration"
                inputProps={{ min: 1, max: 365 }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Permissions</Typography>
              <FormGroup>
                {Object.entries(permissionLabels).map(([key, label]) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Checkbox
                        checked={formData.permissions[key as keyof Permissions] || false}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            permissions: { ...formData.permissions, [key as keyof Permissions]: e.target.checked },
                          })
                        }
                      />
                    }
                    label={label}
                  />
                ))}
              </FormGroup>
            </Grid>

            <Grid item xs={12}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography>Advanced Security Settings</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Allowed IP Addresses (optional)"
                        value={formData.allowed_ips}
                        onChange={(e) => setFormData({ ...formData, allowed_ips: e.target.value })}
                        placeholder="192.168.1.1, 10.0.0.1"
                        helperText="Comma-separated list of IP addresses. Leave empty to allow all IPs."
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Allowed Domains (optional)"
                        value={formData.allowed_domains}
                        onChange={(e) => setFormData({ ...formData, allowed_domains: e.target.value })}
                        placeholder="example.com, api.myapp.com"
                        helperText="Comma-separated list of domains. Leave empty to allow all domains."
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateKey} variant="contained" disabled={!formData.name.trim()}>
            Create API Key
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit API Key Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit API Key</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="API Key Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Rate Limit (requests)"
                type="number"
                value={formData.rate_limit}
                onChange={(e) => setFormData({ ...formData, rate_limit: parseInt(e.target.value) })}
                inputProps={{ min: 1, max: 10000 }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Time Window (seconds)"
                type="number"
                value={formData.rate_limit_window}
                onChange={(e) => setFormData({ ...formData, rate_limit_window: parseInt(e.target.value) })}
                inputProps={{ min: 60, max: 86400 }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Permissions</Typography>
              <FormGroup>
                {Object.entries(permissionLabels).map(([key, label]) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Checkbox
                        checked={formData.permissions[key as keyof Permissions] || false}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            permissions: { ...formData.permissions, [key as keyof Permissions]: e.target.checked },
                          })
                        }
                      />
                    }
                    label={label}
                  />
                ))}
              </FormGroup>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Allowed IP Addresses"
                value={formData.allowed_ips}
                onChange={(e) => setFormData({ ...formData, allowed_ips: e.target.value })}
                placeholder="192.168.1.1, 10.0.0.1"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Allowed Domains"
                value={formData.allowed_domains}
                onChange={(e) => setFormData({ ...formData, allowed_domains: e.target.value })}
                placeholder="example.com, api.myapp.com"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateKey} variant="contained">
            Update API Key
          </Button>
        </DialogActions>
      </Dialog>

      {/* New API Key Display Dialog */}
      <Dialog open={showNewKey} onClose={() => setShowNewKey(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Security color="success" />
            API Key Created Successfully
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <strong>Important:</strong> This is the only time you'll see this API key. 
            Save it securely as it cannot be recovered.
          </Alert>

          {newKeyData && (
            <Box>
              <Typography variant="h6" gutterBottom>Your New API Key:</Typography>
              <Paper sx={{ p: 2, bgcolor: 'grey.100', mb: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {newKeyData.key}
                  </Typography>
                  <Tooltip title="Copy to clipboard">
                    <IconButton onClick={() => copyToClipboard(newKeyData.key)}>
                      <ContentCopy />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Paper>

              <Typography variant="h6" gutterBottom>Key Details:</Typography>
              <List>
                <ListItem>
                  <ListItemIcon><Security /></ListItemIcon>
                  <ListItemText primary="Name" secondary={newKeyData.name} />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Speed /></ListItemIcon>
                  <ListItemText 
                    primary="Rate Limit" 
                    secondary={`${newKeyData.rate_limit} requests per ${Math.floor(newKeyData.rate_limit_window / 3600)} hour(s)`} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Schedule /></ListItemIcon>
                  <ListItemText 
                    primary="Expires" 
                    secondary={newKeyData.expires_at ? formatDate(newKeyData.expires_at) : 'Never'} 
                  />
                </ListItem>
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowNewKey(false)} variant="contained">
            I've Saved It Securely
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApiKeys; 